import React from "react";
import {
  Nav,
  Card,
  Button,
  Dropzone,
  FormInput,
  TabsCustom,
  TabsCustomHeader,
  TabsCustomMenu,
  TabsCustomBody,
  TabsCustomItem,
  FormCheckbox,
} from "ui-kit-ck-consultant";
import FadeIn from "react-fade-in";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faCircle } from "@fortawesome/free-solid-svg-icons";
import { Row, Col } from "react-flexbox-grid";

import AuthContext from "../../../context/AuthContext";

import axiosConfig from "../../../utils/axiosConfig";
import RIGHTS from "../../../permissions/rights.json";

import "./admin-general.css";

// One entry per permission flag, both axes false. The old code hand-listed 38 state
// keys here and forgot glass and statisticsGarage in two of the three places.
const emptyAccess = () =>
  RIGHTS.reduce(
    (access, right) => {
      access.clickable[right.key] = false;
      access.displayed[right.key] = false;
      return access;
    },
    { clickable: {}, displayed: {} }
  );

export default class AdminGeneral extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      picture: null,
      preview: null,
      primaryColor: "#2b5876",
      secondaryColor: "#4e4376",
      // Tabs
      tabsMenu: [],
      tabsIndex: 0,
      access: emptyAccess(),
    };
  }

  static contextType = AuthContext;

  componentDidMount() {
    if (this.context.companyId) {
      this.getCompanyData();
    } else {
      this.getAllCompanies();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.tabsMenu !== this.state.tabsMenu) {
      this.getCompanyData();
    } else if (prevState.tabsIndex !== this.state.tabsIndex) {
      this.getCompanyData();
    }
  }

  getAllCompanies = () => {
    axiosConfig("admin/general/get-all-companies").then((res) => {
      if (res.data.success) {
        this.setState({ tabsMenu: res.data.data });
      } else {
        this.setState({ tabsMenu: [] });
      }
    });
  };

  getCompanyData = () => {
    axiosConfig({
      url: "admin/general/get-company",
      data: {
        companyId:
          this.state.tabsMenu.length > 0
            ? this.state.tabsMenu[this.state.tabsIndex].id
            : null,
      },
    }).then((res) => {
      if (res.data.success) {
        // Rebuilt from scratch for every company, so switching tabs cannot carry one
        // company's flags onto the next — which is exactly what the old partial reset
        // did with glass and statisticsGarage.
        const access = emptyAccess();
        const remote = res.data.data.access || {};
        RIGHTS.forEach((right) => {
          access.clickable[right.key] = !!(remote.clickable || {})[right.key];
          access.displayed[right.key] = !!(remote.displayed || {})[right.key];
        });

        this.setState({
          picture: res.data.data.logo,
          primaryColor: res.data.data.interfaceColors.primary,
          secondaryColor: res.data.data.interfaceColors.secondary,
          access,
        });
      } else {
        this.setState({
          picture: null,
          primaryColor: "#2b5876",
          secondaryColor: "#4e4376",
        });
      }
    });
  };

  setBlobValue = (event) => {
    this.setState({
      dataBlob: event.base64,
      dataMime: event.mime,
      preview: event.base64,
    });
  };

  handleSaveLogoClick = () => {
    axiosConfig({
      url: "admin/general/update-logo",
      data: {
        companyId:
          this.state.tabsMenu.length > 0
            ? this.state.tabsMenu[this.state.tabsIndex].id
            : null,
        base64: this.state.dataBlob,
        mime: this.state.dataMime,
      },
    }).then((res) => {
      if (res.data.success) {
        this.setState({
          picture: this.state.dataBlob,
          preview: null,
        });
        alert("Logo enregistré avec succès");
        if (this.context.companyId) document.location.reload();
      } else {
        alert("Erreur lors de la sauvegarde");
      }
    });
  };

  handleColorInputChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: value });
  };

  handleResetColors = () => {
    this.setState({
      primaryColor: "#2b5876",
      secondaryColor: "#4e4376",
    });
  };

  handleSaveColorClick = () => {
    axiosConfig({
      url: "admin/general/update-colors",
      data: {
        companyId:
          this.state.tabsMenu.length > 0
            ? this.state.tabsMenu[this.state.tabsIndex].id
            : null,
        colors: JSON.stringify({
          primary: this.state.primaryColor,
          secondary: this.state.secondaryColor,
        }),
      },
    }).then((res) => {
      if (res.data.success) {
        alert("Nouvelles couleurs sauvegardées avec succès");
        this.getCompanyData();
        if (this.context.companyId) document.location.reload();
      } else {
        alert("Erreur lors de la sauvegarde");
      }
    });
  };

  // The one rule the old 21-branch handler restated for every flag: a page you cannot
  // see cannot be opened, and a page you can open must be visible.
  handleAccessChange = (axis, key, checked) =>
    this.setState((prev) => {
      const access = {
        clickable: { ...prev.access.clickable },
        displayed: { ...prev.access.displayed },
      };

      access[axis][key] = checked;
      if (axis === "displayed" && !checked) access.clickable[key] = false;
      if (axis === "clickable" && checked) access.displayed[key] = true;

      return { access };
    });

  handleEditAccessClick = () => {
    // companyId stays in the payload: this grid only renders for the global
    // super-admin, who has no company of their own for the server to fall back on.
    const companyId = this.state.tabsMenu.length
      ? this.state.tabsMenu[this.state.tabsIndex].id
      : null;

    axiosConfig({
      url: "admin/general/update-access",
      data: {
        companyId,
        access: JSON.stringify(this.state.access),
      },
    }).then((res) => {
      if (res.data.success) {
        alert("Droits d'accès sauvegardés avec succès");
        this.getCompanyData();
      } else {
        alert("Erreur lors de la sauvegarde");
      }
    });
  };

  renderAccessCard = (axis, title) => (
    <Card title={title} className="mb-20">
      <Row>
        {RIGHTS.map((right) => (
          <Col xs={12} md={6} key={`${axis}-${right.key}`}>
            <Row>
              <FormCheckbox
                className="mb-20"
                name={`${axis}.${right.key}`}
                checked={this.state.access[axis][right.key]}
                text={right.label}
                onChange={(event) =>
                  this.handleAccessChange(axis, right.key, event.target.checked)
                }
              />
            </Row>
          </Col>
        ))}
      </Row>
      <Button
        className="mb-4"
        text={"Modifier"}
        onClick={this.handleEditAccessClick}
      />
    </Card>
  );

  render() {
    if (this.context.id) {
      return (
        <>
          <FadeIn>
            <Nav title="Général" className="mb-20" />

            {this.context.companyId ? (
              <Row>
                {/* UPDATE LOGO */}
                <Col xs={12} md={6}>
                  <Card title="Logo" className="mb-20">
                    <div className="container">
                      {this.state.picture ? (
                        <img
                          style={{ width: "200px", position: "relative" }}
                          src={this.state.picture}
                          alt="logo"
                        />
                      ) : (
                        <FontAwesomeIcon icon={faBuilding} size="6x" />
                      )}
                      <Dropzone
                        className={"m-auto my-10"}
                        dropHandler={this.setBlobValue}
                        typeName={["png"]}
                        typeAccept={["image/png"]}
                      />
                      {this.state.dataBlob ? (
                        <>
                          {this.state.preview ? (
                            <div>
                              <h4>Aperçu : </h4>
                              <img
                                style={{ width: "100px", position: "relative" }}
                                src={this.state.preview}
                                alt="logo"
                              />
                            </div>
                          ) : null}
                          <Button
                            text="Valider"
                            onClick={this.handleSaveLogoClick}
                          />
                        </>
                      ) : null}
                    </div>
                  </Card>
                </Col>

                {/* EDIT COLORS */}
                <Col xs={12} md={6}>
                  <Card title="Couleurs" className="mb-20">
                    <Row>
                      <Col xs={12} md={6}>
                        <FormInput
                          className="mb-20"
                          type="color"
                          name="primaryColor"
                          value={this.state.primaryColor}
                          title="Couleur primaire"
                          onChange={this.handleColorInputChange}
                        />
                      </Col>
                      <Col xs={12} md={6}>
                        <FormInput
                          className="mb-20"
                          type="color"
                          name="secondaryColor"
                          value={this.state.secondaryColor}
                          title="Couleur secondaire"
                          onChange={this.handleColorInputChange}
                        />
                      </Col>
                    </Row>
                    <div style={{ display: "flex", marginLeft: "auto" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          marginLeft: "auto",
                        }}
                      >
                        <button
                          className="button-color"
                          style={{
                            backgroundColor: this.state.primaryColor,
                            borderColor: this.state.primaryColor,
                          }}
                          onClick={this.handleSaveColorClick}
                        >
                          <span>Modifier la couleur</span>
                          <div
                            style={{
                              backgroundColor: this.state.secondaryColor,
                            }}
                          >
                            <FontAwesomeIcon icon={faCircle} size="lg" />
                          </div>
                        </button>
                        <span
                          className="reset-colors"
                          style={{ color: this.state.primaryColor }}
                          onClick={this.handleResetColors}
                        >
                          Couleurs par défaut
                        </span>
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>
            ) : (
              <TabsCustom>
                {/* COMPANY SELECT */}
                <TabsCustomHeader>
                  {this.state.tabsMenu.map((menu, index) => (
                    <TabsCustomMenu
                      key={index}
                      title={menu.name}
                      active={this.state.tabsIndex === index}
                      onClick={() => this.setState({ tabsIndex: index })}
                    />
                  ))}
                </TabsCustomHeader>

                {/* COMPANY EDIT */}
                <TabsCustomBody noCard>
                  {this.state.tabsMenu.map((element, idx) => (
                    <TabsCustomItem
                      key={idx}
                      active={this.state.tabsIndex === idx}
                    >
                      <Row>
                        {/* UPDATE LOGO */}
                        <Col xs={12} md={6}>
                          <Card title="Logo" className="mb-20">
                            <div className="container">
                              {this.state.picture ? (
                                <img
                                  style={{
                                    width: "200px",
                                    position: "relative",
                                  }}
                                  src={this.state.picture}
                                  alt="logo"
                                />
                              ) : (
                                <FontAwesomeIcon icon={faBuilding} size="6x" />
                              )}
                              <Dropzone
                                className={"m-auto my-10"}
                                dropHandler={this.setBlobValue}
                                typeName={["png"]}
                                typeAccept={["image/png"]}
                              />
                              {this.state.dataBlob ? (
                                <>
                                  {this.state.preview ? (
                                    <div>
                                      <h4>Aperçu : </h4>
                                      <img
                                        style={{
                                          width: "100px",
                                          position: "relative",
                                          borderRadius: "50%",
                                        }}
                                        src={this.state.preview}
                                        alt="logo"
                                      />
                                    </div>
                                  ) : null}
                                  <Button
                                    text="Valider"
                                    onClick={this.handleSaveLogoClick}
                                  />
                                </>
                              ) : null}
                            </div>
                          </Card>
                        </Col>

                        {/* EDIT COLORS */}
                        <Col xs={12} md={6}>
                          <Card title="Couleurs" className="mb-20">
                            <Row>
                              <Col xs={12} md={6}>
                                <FormInput
                                  className="mb-20"
                                  type="color"
                                  name="primaryColor"
                                  value={this.state.primaryColor}
                                  title="Couleur primaire"
                                  onChange={this.handleColorInputChange}
                                />
                              </Col>
                              <Col xs={12} md={6}>
                                <FormInput
                                  className="mb-20"
                                  type="color"
                                  name="secondaryColor"
                                  value={this.state.secondaryColor}
                                  title="Couleur secondaire"
                                  onChange={this.handleColorInputChange}
                                />
                              </Col>
                            </Row>
                            <div
                              style={{ display: "flex", marginLeft: "auto" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  marginLeft: "auto",
                                }}
                              >
                                <button
                                  className="button-color"
                                  style={{
                                    backgroundColor: this.state.primaryColor,
                                    borderColor: this.state.primaryColor,
                                  }}
                                  onClick={this.handleSaveColorClick}
                                >
                                  <span>Modifier la couleur</span>
                                  <div
                                    style={{
                                      backgroundColor:
                                        this.state.secondaryColor,
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faCircle}
                                      size="lg"
                                    />
                                  </div>
                                </button>
                                <span
                                  className="reset-colors"
                                  style={{ color: this.state.primaryColor }}
                                  onClick={this.handleResetColors}
                                >
                                  Couleurs par défaut
                                </span>
                              </div>
                            </div>
                          </Card>
                        </Col>

                        {/* COMPANY ACCESS — both grids are generated from
                            permissions/rights.json, so a new page's toggles show up
                            here the day it is added to the registry. */}
                        <Col xs={12} md={6}>
                          {this.renderAccessCard("displayed", "Affichage")}
                        </Col>

                        <Col xs={12} md={6}>
                          {this.renderAccessCard("clickable", "Accès")}
                        </Col>
                      </Row>
                    </TabsCustomItem>
                  ))}
                </TabsCustomBody>
              </TabsCustom>
            )}
          </FadeIn>
        </>
      );
    } else {
      return <></>;
    }
  }
}
